import { PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { type ReactElement, useEffect, useRef } from 'react';
import { Button, cn } from '@/shared/ui';
import { DESKTOP_NAV_ID, MOBILE_DRAWER_ID } from './nav-ids';
import { SidebarBrand } from './sidebar-brand';
import { SidebarNav } from './sidebar-nav';

/** 흑연 바탕 위 흰 아이콘·글자 버튼(접기·닫기). */
const ON_DARK_BUTTON_CLASS = 'text-white/70 hover:bg-white/[0.06] hover:text-white';

/** 드로어 포커스 트랩이 순회 대상으로 삼는 요소. */
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface AdminSidebarProps {
  /** 데스크톱에서 아이콘 레일로 접힌 상태인지. */
  readonly isCollapsed: boolean;
  readonly onToggleCollapse: () => void;
  /** 모바일 오버레이 드로어가 열린 상태인지. */
  readonly isMobileOpen: boolean;
  readonly onMobileClose: () => void;
}

/**
 * ADMIN 사이드바 GNB.
 *
 * 상태는 소유하지 않는 프레젠테이셔널 위젯 — 접힘 · 드로어 상태는
 * 레이아웃(app/admin-layout)이 소유하고 props 로 내려준다.
 * 데스크톱(md↑)은 정적 사이드바(접으면 아이콘 레일), 모바일은 오버레이 드로어.
 * 고객 웹 푸터·로그인 브랜드 면과 같은 흑연 바탕에 맨 위 로고, 선택 메뉴는 라임으로 표시한다.
 */
export const AdminSidebar = ({
  isCollapsed,
  onToggleCollapse,
  isMobileOpen,
  onMobileClose,
}: AdminSidebarProps): ReactElement => {
  const drawerRef = useRef<HTMLElement | null>(null);

  // aria-modal 계약 이행 — 열리면 포커스를 드로어로 옮기고 Tab 을 안에 가두며,
  // 배경 스크롤을 잠그고, 닫히면 트리거(햄버거)로 포커스를 되돌린다.
  useEffect(() => {
    if (!isMobileOpen) {
      return;
    }

    const drawer = drawerRef.current;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    drawer?.focus();

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onMobileClose();
        return;
      }

      if (event.key !== 'Tab' || !drawer) {
        return;
      }

      const focusables = drawer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);

      if (focusables.length === 0) {
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !drawer.contains(active)) {
          event.preventDefault();
          last?.focus();
        }
      } else if (active === last || !drawer.contains(active)) {
        event.preventDefault();
        first?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      previouslyFocused?.focus();
    };
  }, [isMobileOpen, onMobileClose]);

  return (
    <>
      {/* 데스크톱 — 화면 높이를 차지하는 고정 사이드바. */}
      <aside
        className={cn(
          'hidden shrink-0 flex-col bg-primary text-white md:flex',
          isCollapsed ? 'w-16 px-2' : 'w-64 px-3',
        )}
      >
        <SidebarBrand isCollapsed={isCollapsed} />

        <SidebarNav isCollapsed={isCollapsed} label="관리자 메뉴" id={DESKTOP_NAV_ID} />

        <div className="border-t border-white/10 py-3">
          <Button
            variant="ghost"
            size={isCollapsed ? 'icon' : 'sm'}
            aria-label={isCollapsed ? '사이드바 펼치기' : '사이드바 접기'}
            aria-expanded={!isCollapsed}
            aria-controls={DESKTOP_NAV_ID}
            onClick={onToggleCollapse}
            className={cn(
              'w-full rounded-lg',
              ON_DARK_BUTTON_CLASS,
              !isCollapsed && 'justify-start gap-2.5 px-3',
            )}
          >
            {isCollapsed ? (
              <PanelLeftOpen aria-hidden="true" />
            ) : (
              <>
                <PanelLeftClose aria-hidden="true" />
                접기
              </>
            )}
          </Button>
        </div>
      </aside>

      {/* 모바일 — 오버레이 드로어. 백드롭 · 닫기 버튼 · 항목 이동 · Escape 로 닫힌다. */}
      {isMobileOpen ? (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            data-testid="admin-sidebar-backdrop"
            className="absolute inset-0 bg-foreground/40"
            onClick={onMobileClose}
            aria-hidden="true"
          />

          <aside
            ref={drawerRef}
            id={MOBILE_DRAWER_ID}
            role="dialog"
            aria-modal="true"
            aria-label="관리자 메뉴"
            tabIndex={-1}
            className="absolute inset-y-0 left-0 flex w-72 flex-col bg-primary px-3 text-white shadow-lg outline-none"
          >
            <div className="flex shrink-0 items-center justify-between">
              <SidebarBrand isCollapsed={false} onNavigate={onMobileClose} />
              <Button
                variant="ghost"
                size="icon"
                aria-label="메뉴 닫기"
                onClick={onMobileClose}
                className={ON_DARK_BUTTON_CLASS}
              >
                <X aria-hidden="true" />
              </Button>
            </div>

            <SidebarNav isCollapsed={false} label="모바일 관리자 메뉴" onNavigate={onMobileClose} />
          </aside>
        </div>
      ) : null}
    </>
  );
};
